/**
 * ApiMapper - Simple 1:1 mapping from Jataka API to ReadyChartData
 *
 * No validation needed - server guarantees data correctness
 * Just transforms API structure to frontend rendering structure
 */

import { devWarn } from './devLog';
import { GrahaPosition, GrahaName, Coordinate, RasiName } from '../models/types';
import { parseDMSToCoordinate } from './astrology/coordinates';
import {
    JatakaApiResponse,
    ApiGrahaPosition,
    extractHoraFromApi,
    validateJatakaApiResponse
} from '../types/JatakaApiTypes';
import {
    ReadyChartData,
    ReadyHoraData,
    ReadyMuhurtaData,
    ReadyAstronomicalData,
    ReadyChartMetadata
} from '../types/ReadyChartData';

/**
 * Maps API graha short names to internal GrahaName enum
 */
const GRAHA_NAME_MAPPING: Record<string, GrahaName> = {
    'Sy': GrahaName.Sy,
    'Ch': GrahaName.Ch,
    'Ma': GrahaName.Ma,
    'Bu': GrahaName.Bu,
    'Gu': GrahaName.Gu,
    'Sk': GrahaName.Sk,
    'Sa': GrahaName.Sa,
    'Ra': GrahaName.Rahu,
    'Ke': GrahaName.Ketu,
    'Lg': GrahaName.Lg
};

/**
 * Maps API rasi names to internal RasiName enum
 */
const RASI_NAME_MAPPING: Record<string, RasiName> = {
    'Mesha': RasiName.Mesha,
    'Vrishabha': RasiName.Vrishabha,
    'Mithuna': RasiName.Mithuna,
    'Karka': RasiName.Karka,
    'Simha': RasiName.Simha,
    'Kanya': RasiName.Kanya,
    'Tula': RasiName.Tula,
    'Vrishchika': RasiName.Vrishchika,
    'Dhanu': RasiName.Dhanu,
    'Makara': RasiName.Makara,
    'Kumbha': RasiName.Kumbha,
    'Meena': RasiName.Meena
};


/**
 * Map API graha position to internal GrahaPosition
 */
function mapApiGrahaPosition(apiGraha: ApiGrahaPosition): GrahaPosition {
    const graha = GRAHA_NAME_MAPPING[apiGraha.short_name] || apiGraha.short_name as GrahaName;
    const rasi = RASI_NAME_MAPPING[apiGraha.rasi_name] || apiGraha.rasi_name as RasiName;

    return {
        graha,
        position: parseDMSToCoordinate(apiGraha.longitude_dms),
        positionString: apiGraha.position, // Unified format for animations
        rasi,
        nakshatra: apiGraha.nakshatra_name as any, // API guarantees correct nakshatra names
        isRetrograde: apiGraha.is_retrograde,
        metadata: {
            longitude: apiGraha.longitude,
            latitude: apiGraha.latitude,
            speed: apiGraha.speed,
            rasi_number: apiGraha.rasi_number,
            nakshatra_number: apiGraha.nakshatra_number,
            nakshatra_pada: apiGraha.nakshatra_pada,
            navamsa_rasi: apiGraha.navamsa_rasi,
            muhurta_num: apiGraha.muhurta_num,
            muhurta_graha: apiGraha.muhurta_graha,
            muhurta_lord: apiGraha.muhurta_lord
        }
    };
}

/**
 * Extract Lagna from planets array or create from separate lagna field
 */
function extractLagna(apiResponse: JatakaApiResponse): GrahaPosition {
    // First try to find Lagna in planets array
    const lagnaInPlanets = apiResponse.graha_positions.planets.find(
        planet => planet.short_name === 'Lg'
    );

    if (lagnaInPlanets) {
        return mapApiGrahaPosition(lagnaInPlanets);
    }

    // If separate lagna field exists, use it
    if (apiResponse.graha_positions.lagna) {
        return mapApiGrahaPosition(apiResponse.graha_positions.lagna);
    }

    // Fallback: create default Lagna (should not happen with proper API)
    devWarn('⚠️ ApiMapper: No Lagna found in API response');
    return {
        graha: GrahaName.Lg,
        position: { degrees: 0, minutes: 0, seconds: 0 },
        rasi: RasiName.Mesha,
        isRetrograde: false,
        metadata: {}
    };
}

/**
 * Map hora data from API panchanga and detailed_panchanga
 */
function mapHoraData(apiResponse: JatakaApiResponse): ReadyHoraData | undefined {
    const horaData = extractHoraFromApi(apiResponse.panchanga, apiResponse.detailed_panchanga);
    if (!horaData) return undefined;

    const graha = horaData.graha ? GRAHA_NAME_MAPPING[horaData.graha] || null : null;

    return {
        graha,
        lord: horaData.lord,
        currentPlanet: horaData.currentPlanet  // Add current_planet from API
    };
}

/**
 * Map muhurta data from API detailed_panchanga
 */
function mapMuhurtaData(apiResponse: JatakaApiResponse): ReadyMuhurtaData | undefined {
    const muhurtaDetails = apiResponse.detailed_panchanga?.muhurta_details;
    if (!muhurtaDetails || !muhurtaDetails.chakra_longitude) {
        return undefined;
    }

    return {
        sunriseLongitude: apiResponse.astronomical.sunrise_surya_longitude.toString(),
        currentMuhurta: {
            number: muhurtaDetails.number,
            name: muhurtaDetails.name,
            graha_ruler: muhurtaDetails.graha_ruler,
            lord: muhurtaDetails.lord,
            completion: muhurtaDetails.completion,
            chakra_longitude: muhurtaDetails.chakra_longitude
        },
        isAvailable: true
    };
}

/**
 * Map astronomical data (direct passthrough - API provides perfect format)
 */
function mapAstronomicalData(apiResponse: JatakaApiResponse): ReadyAstronomicalData {
    return {
        sunrise_surya_longitude: apiResponse.astronomical.sunrise_surya_longitude.toString(),
        sunrise: apiResponse.astronomical.sunrise,
        sunset: apiResponse.astronomical.sunset,
        ayanamsa: apiResponse.astronomical.ayanamsa,
        sidereal_time: apiResponse.astronomical.sidereal_time,
        julian_day: apiResponse.astronomical.julian_day
    };
}

/**
 * Map chart metadata from API basic_info
 */
function mapChartMetadata(apiResponse: JatakaApiResponse, chartId: string): ReadyChartMetadata {
    return {
        chartId,
        chartType: 'D1', // Default chart type, can be parameterized later
        calculationMethod: 'SwissEphemeris',
        timestamp: Date.now(),
        location: {
            place_name: apiResponse.basic_info.place_name || 'Unknown Location',
            latitude: apiResponse.basic_info.latitudeValue,
            longitude: apiResponse.basic_info.longitudeValue,
            timezone: apiResponse.basic_info.timezone
        },
        datetime: {
            date: apiResponse.basic_info.date,
            time: apiResponse.basic_info.time
        }
    };
}

/**
 * Main mapping function: JatakaApiResponse → ReadyChartData
 */
export class ApiMapper {
    /**
     * Map complete API response to ReadyChartData
     * Enhanced validation with better error messages
     */
    static toReadyChartData(
        apiResponse: JatakaApiResponse,
        chartId: string,
        eventSource: string = 'api-mapped'
    ): ReadyChartData {
        // Enhanced validation with detailed error reporting
        const validation = validateJatakaApiResponse(apiResponse);

        if (!validation.isValid) {
            if (validation.dataType === 'legacy_chart') {
                throw new Error(`Legacy chart data detected. Use legacy chart handling instead of ApiMapper. ${validation.errors.join(', ')}`);
            }
            throw new Error(`Invalid JatakaApiResponse: ${validation.errors.join(', ')}`);
        }

        if (validation.warnings.length > 0) {
            devWarn(`⚠️ ApiMapper warnings:`, validation.warnings);
        }

        // Extract Lagna separately
        const lagna = extractLagna(apiResponse);

        // Map planets (excluding Lagna)
        const planets = apiResponse.graha_positions.planets
            .filter(planet => planet.short_name !== 'Lg')
            .map(mapApiGrahaPosition);

        // Map optional data
        const hora = mapHoraData(apiResponse);
        const muhurta = mapMuhurtaData(apiResponse);

        // Map required data
        const astronomical = mapAstronomicalData(apiResponse);
        const metadata = mapChartMetadata(apiResponse, chartId);

        return {
            planets,
            lagna,
            astronomical,
            metadata,
            hora,
            muhurta,
            detailed_panchanga: apiResponse.detailed_panchanga,  // Pass raw API data for components that need it
            rawApiPlanets: apiResponse.graha_positions.planets,  // Raw API planet data with full names
            eventSource,
            chartId,
            timestamp: Date.now()
        };
    }

    /**
     * Quick check if API response has all required fields
     */
    static validateApiResponse(apiResponse: any): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        if (!apiResponse) {
            errors.push('API response is null or undefined');
            return { isValid: false, errors };
        }

        if (!apiResponse.graha_positions?.planets) {
            errors.push('Missing graha_positions.planets');
        }

        if (!apiResponse.astronomical?.sunrise_surya_longitude) {
            errors.push('Missing astronomical.sunrise_surya_longitude');
        }

        if (!apiResponse.basic_info?.place_name) {
            errors.push('Missing basic_info.place_name');
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    /**
     * Helper to extract just the planets data for quick access
     */
    static extractPlanetsOnly(apiResponse: JatakaApiResponse): GrahaPosition[] {
        return apiResponse.graha_positions.planets
            .filter(planet => planet.short_name !== 'Lg')
            .map(mapApiGrahaPosition);
    }

    /**
     * Helper to extract just the muhurta longitude for quick access
     */
    static extractMuhurtaLongitude(apiResponse: JatakaApiResponse): string {
        return apiResponse.astronomical.sunrise_surya_longitude.toString();
    }

    // Legacy converter removed - only JatakaApiResponse supported
}